import type { CSSProperties, ReactElement } from "react";
import { color, radius, space } from "@tokens/index";
import { Text, useViewport } from "@primitives/index";
import type { TimelineEvent } from "../../../experience-shell/workflows/timeline";
import { describeEvent, toneColor } from "./describeEvent";
import { formatTime } from "./grouping";

// A flat row: the glyph rail and spacing separate events; no border, no
// fill. The day header above the group carries the only hairline.
function rowStyle(trailingTime: boolean): CSSProperties {
  return {
    display: "grid",
    gridTemplateColumns: trailingTime ? "auto minmax(0, 1fr) auto" : "auto minmax(0, 1fr)",
    gap: space[3],
    alignItems: "start",
    paddingBlock: space[2],
    paddingInline: space[1],
    minWidth: 0,
  };
}

// The headline's first line centres on the 32px glyph.
const contentStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: space[1],
  minWidth: 0,
  paddingTop: "6px",
};

const trailingTimeStyle: CSSProperties = {
  paddingTop: "8px",
  minWidth: "48px",
  textAlign: "right",
  whiteSpace: "nowrap",
};

function glyphStyle(tone: string): CSSProperties {
  return {
    width: "32px",
    height: "32px",
    borderRadius: radius.full,
    background: `color-mix(in oklch, ${tone} 14%, ${color.bg.surface2})`,
    color: tone,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  };
}

/**
 * One audit event. Glyph + content on every class; the time is a muted
 * trailing cell on `medium` and wider, and the first token of the meta line
 * under the headline on `compact`.
 */
export function TimelineRow({ event }: { readonly event: TimelineEvent }): ReactElement {
  const { isCompact } = useViewport();
  const descriptor = describeEvent(event);
  const Icon = descriptor.icon;
  const iconColor = toneColor(descriptor.tone);
  const time = formatTime(event.occurredAt);
  const trailingTime = !isCompact;

  return (
    <div style={rowStyle(trailingTime)}>
      <span aria-hidden="true" style={glyphStyle(iconColor)}>
        <Icon size={16} strokeWidth={2.25} />
      </span>
      <div style={contentStyle}>
        <Text role="bodySm" style={{ overflowWrap: "anywhere" }}>
          {descriptor.headline}
        </Text>
        {trailingTime ? null : (
          <Text role="caption" tone="subtle" tabular>
            {time}
          </Text>
        )}
        {descriptor.body !== undefined ? descriptor.body : null}
      </div>
      {trailingTime ? (
        <Text role="caption" tone="subtle" tabular style={trailingTimeStyle}>
          {time}
        </Text>
      ) : null}
    </div>
  );
}
